/**
 * THE PLAIN-ENGLISH SUMMARY — the dashboard's numbers, said in sentences.
 *
 * <h2>The one rule everything here obeys: it may not find something that is not there</h2>
 *
 * A narrative layer is the easiest place in this whole feature to do damage. Prose is persuasive
 * in a way a table is not — a reader who would have checked an interval beside a percentage will
 * accept the same percentage inside a sentence — so a summariser that always has a finding would
 * turn the most carefully hedged panel in the application into its most misleading one.
 *
 * <p>So a result reaches the ACTIONABLE section only if its Wilson interval clears the baseline
 * it is measured against, or in the case of a filter, only if both sides of the comparison have
 * enough observations to be compared. Everything else is reported as WATCHING, with the reason
 * it did not qualify. And when nothing qualifies, the summary says so in as many words rather
 * than reaching for the best of a bad set — which is the sentence this module exists to be able
 * to write.
 *
 * <h2>It computes nothing</h2>
 *
 * Every number below is read off the report. A second derivation here would be a second answer
 * to a question the engine has already answered carefully, a centimetre from the table that
 * disagrees with it — the failure this repository recorded between the Running Ledger and the
 * Simulator, in the worst possible place.
 */

import { fixed, pct } from './analyticaStats';

/** Below this, a comparison is an anecdote. Named so the threshold is arguable. */
export const MIN_COMPARABLE = 20;

/** A lift smaller than this is not worth a sentence however many observations produced it. */
export const MATERIAL_LIFT = 0.05;

const n = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const plural = (c, word) => `${c} ${word}${c === 1 ? '' : 's'}`;

/**
 * The whole summary, as sections the dialog renders in order.
 *
 * @returns {Array<{id:string, heading:string, tone:string, lines:string[]}>}
 */
export function narrate(report) {
  if (!report) return [];
  if (!report.ok) {
    return [{ id: 'refused', heading: 'This scan did not run', tone: 'caution',
      lines: [report.reason] }];
  }
  const findings = collectFindings(report);
  return [
    scopeSection(report),
    channelSection(report),
    timingSection(report),
    actionableSection(findings),
    limitsSection(report, findings),
  ].filter(Boolean);
}

/** The single sentence at the head of the tab. Deliberately refuses to overstate. */
export function headline(report) {
  if (!report || !report.ok) return 'Chart Analytica could not run on this range.';
  const { confirmed } = collectFindings(report);
  const where = `${report.symbol || 'this instrument'} to ${report.clickedDate}`;
  if (confirmed.length === 0) {
    return `${report.scannedBars.toLocaleString()} bars of ${where} scanned. `
      + 'Nothing in this range clears its own baseline once the sample size is taken into '
      + 'account — read the tables for shape, not for an edge.';
  }
  return `${report.scannedBars.toLocaleString()} bars of ${where} scanned. `
    + `${plural(confirmed.length, 'result')} clear${confirmed.length === 1 ? 's' : ''} `
    + 'the baseline with the interval to support it; the rest is context.';
}

/* ─────────────────────────────────────── the sections ─────────────────────────────────────── */

function scopeSection(report) {
  const b = report.bars;
  if (!b) return null;
  const lines = [
    `You scanned ${b.bars.toLocaleString()} bars, from ${b.firstDate} to ${b.lastDate}.`
    + ` Over that stretch price ran between ${fixed(b.low)} and ${fixed(b.high)}`
    + `${n(b.totalReturnPct) === null ? '' : `, a ${fixed(b.totalReturnPct, 1)}% move end to end`}.`,
  ];
  if (b.trend.state === 'UNKNOWN') {
    lines.push('There are fewer than 200 bars here, so the 50/200 regime cannot be computed — '
      + 'and anything in this report that depends on the market phase rests on nothing.');
  } else {
    lines.push(`At the bar you clicked the 50-bar average was ${b.trend.state === 'ACCUMULATION'
      ? 'above' : b.trend.state === 'DISTRIBUTION' ? 'below' : 'exactly on'} the 200-bar one`
      + ` — ${b.trend.state}. Counter-trend signals in this range were fighting that.`);
  }
  const have = [];
  if (report.inputs.bands > 0) {
    have.push(`${plural(report.inputs.bands, 'channel')} `
      + `(${report.inputs.bandCategories.join(' and ')})`);
  }
  if (report.inputs.ctsPoints > 0) have.push(`${report.inputs.ctsPoints} CTS timing points`);
  if (report.inputs.rsi) have.push('RSI');
  if (report.inputs.volume) have.push('volume');
  lines.push(have.length > 0
    ? `The scan had ${have.join(', ')} to work with.`
    : 'The scan had no structure to work with at all — only the bars themselves.');
  return { id: 'scope', heading: 'What you scanned', tone: 'neutral', lines };
}

function channelSection(report) {
  const band = report.band;
  if (!band) {
    return { id: 'channel', heading: 'The channel', tone: 'caution',
      lines: ['No PTA or PTB cycle is drawn on this chart, so none of the channel geometry could '
        + 'be measured. Click PTA — Long Cycle or PTB — Short Cycle and run the scan again.'] };
  }
  const lines = [];
  const ex = band.excursion;

  lines.push(`${plural(band.sections, 'channel section')} were in force across the range`
    + `${band.clippedSections > 0
      ? `, ${band.clippedSections} of them still open at the bar you clicked` : ''}.`);

  if (n(ex.longAtLeast1x.rate) !== null) {
    lines.push('Price cleared the channel edge by a full band height in '
      + `${pct(ex.longAtLeast1x.rate)} of sections on the long side and `
      + `${pct(ex.shortAtLeast1x.rate)} on the short. The median run beyond the edge was `
      + `${fixed(ex.medianLong)} band heights up and ${fixed(ex.medianShort)} down.`
      + ' That distribution is what sizes a target: a 1x objective fills about as often as the '
      + 'first of those numbers says.');
  }

  const eq = band.equator;
  if (eq.touches > 0 && n(eq.penetrationRate.rate) !== null) {
    const through = eq.penetrationRate.rate;
    lines.push(`The midline was touched on ${eq.touches.toLocaleString()} bars, and `
      + `${pct(through)} of those touches ended with a close straight through it. `
      + (through > 0.55
        ? 'The midline was being traversed rather than respected here, which favours trading '
          + 'through it over fading it.'
        : through < 0.45
          ? 'The midline turned price back more often than it let it through — a level to fade '
            + 'rather than to trade through.'
          : 'It held and gave way in roughly equal measure, so on this range the midline is not '
            + 'a level to lean on either way.'));
  }

  const best = strongestLayer(band);
  if (best) {
    lines.push(`The firmest boundary was ${best.layer} on the ${best.side} side, which turned `
      + `price back ${pct(best.hold.rate)} of the ${plural(best.tests, 'time')} it was tested.`
      + (best.tests < MIN_COMPARABLE
        ? ' That is a small number of tests, so treat it as a hint rather than a level.'
        : ''));
  }
  return { id: 'channel', heading: 'What the channel did', tone: 'neutral', lines };
}

function timingSection(report) {
  const cts = report.cts;
  if (!cts) {
    return { id: 'timing', heading: 'The timing study', tone: 'caution',
      lines: ['No Critical Timing Scan is on this chart, so there were no timing points to '
        + 'score. Run the CTS study and scan again.'] };
  }
  const lines = [];
  const base = cts.baseline;

  lines.push(`${plural(cts.total, 'timing point')} fell inside the range`
    + `${cts.unresolved > 0
      ? `, ${cts.unresolved} of them too recent for a ${cts.horizon}-bar outcome and therefore `
        + 'left out of every rate below'
      : ''}.`);

  if (n(base.up) !== null) {
    lines.push('Before reading any success rate, the bar to clear: over these same '
      + `${base.n.toLocaleString()} bars price simply closed HIGHER ${cts.horizon} bars later `
      + `${pct(base.up)} of the time, and lower ${pct(base.down)} of the time. A long signal `
      + `winning less than ${pct(base.up)} was worse than buying at random.`);
  }

  ['upsideByZone', 'downsideByZone'].forEach((key) => {
    const side = key === 'upsideByZone' ? 'Upside' : 'Downside';
    const zones = (cts[key]?.zones || []).filter(z => z.total > 0);
    if (zones.length === 0) return;
    const clears = zones.filter(z => z.verdict === 'above');
    const bestZone = [...zones].sort((a, b) => (n(b.edge) ?? -9) - (n(a.edge) ?? -9))[0];
    if (clears.length > 0) {
      const top = [...clears].sort((a, b) => (n(b.edge) ?? -9) - (n(a.edge) ?? -9))[0];
      lines.push(`${side} reversals did best in ${top.zone.replace('_', ' ')}: `
        + `${pct(top.rate)} of ${plural(top.total, 'resolved signal')} paid, which is `
        + `${fixed((top.edge || 0) * 100, 1)} points above the baseline and clears it even `
        + 'allowing for the sample size.');
    } else if (bestZone) {
      lines.push(`${side} reversals were best in ${bestZone.zone.replace('_', ' ')} at `
        + `${pct(bestZone.rate)} of ${plural(bestZone.total, 'signal')}, but no zone on this `
        + 'side separates from the baseline once its interval is taken into account — so '
        + 'treat the differences between them as noise for now.');
    }
  });

  const d = cts.distanceVsReversion;
  if (d && d.buckets.length > 1 && n(d.overall.rate) !== null) {
    const first = d.buckets[0];
    const last = [...d.buckets].reverse().find(b => b.total > 0);
    if (first.total > 0 && last && last !== first
      && first.ci && last.ci && last.ci.low > first.ci.high) {
      lines.push('Distance from the midline matters here: a signal firing within half a band '
        + `height of it reverted ${pct(first.rate)} of the time, against ${pct(last.rate)} for `
        + `one firing ${last.label}. Those two ranges do not overlap, so the difference is real.`);
    }
  }
  return { id: 'timing', heading: 'What the timing study did', tone: 'neutral', lines };
}

function actionableSection({ confirmed, watching }) {
  const lines = [];
  if (confirmed.length === 0) {
    lines.push('NOTHING in this range clears its own baseline once the sample size is taken into '
      + 'account. That is a finding rather than a failure of the scan: it says the structure '
      + 'here describes the market\'s shape without yet distinguishing a setup worth sizing up.');
  } else {
    confirmed.forEach(f => lines.push(f.text));
  }
  if (watching.length > 0) {
    lines.push('—');
    lines.push('WORTH WATCHING, but not yet established:');
    watching.forEach(f => lines.push(f.text));
  }
  return {
    id: 'actionable',
    heading: 'What you can act on',
    tone: confirmed.length > 0 ? 'positive' : 'caution',
    lines,
  };
}

function limitsSection(report, { confirmed }) {
  const lines = [];
  const missing = Object.values(report.unavailable || {});
  const unique = [...new Set(missing)];
  unique.forEach(reason => lines.push(reason));

  if (report.cts && report.cts.unresolved > 0) {
    lines.push(`${plural(report.cts.unresolved, 'timing point')} near the right-hand edge have `
      + 'no outcome yet. They are live setups, not failures.');
  }
  if (report.edge && n(report.edge.separation) !== null) {
    lines.push('The Edge Score is IN-SAMPLE: its components are worth what they were measured to '
      + 'be worth on these very signals, so the separation between its High and Low tiers is '
      + 'arithmetic rather than evidence. Use it to rank setups on this chart, never as a '
      + 'probability and never on another instrument.');
  }
  lines.push('Signals cluster, so forward windows overlap and two points a few bars apart share '
    + 'most of their outcome. That is why this panel reports confidence intervals rather than '
    + 'p-values, and why a comparison against the baseline on the same bars is the most '
    + 'trustworthy thing here.');
  if (confirmed.length > 0) {
    lines.push('And everything above is ONE instrument over ONE range. A result that clears its '
      + 'baseline here has not been shown to clear it anywhere else.');
  }
  return { id: 'limits', heading: 'What this scan cannot tell you', tone: 'caution', lines };
}

/* ────────────────────────────────────── the findings ──────────────────────────────────────── */

/**
 * Splits every candidate result into CONFIRMED and WATCHING.
 *
 * <p>The gate is the same one the tables use — a Wilson interval clearing the baseline, or both
 * sides of a filter comparison carrying enough observations to be compared. A result that fails
 * it is not discarded: it is listed as watching WITH the reason, because "we looked and the
 * sample was too small" is information and silence is not.
 */
export function collectFindings(report) {
  const confirmed = [];
  const watching = [];
  if (!report || !report.ok) return { confirmed, watching };
  const cts = report.cts;

  // ── zones that beat the baseline ───────────────────────────────────────────────────────
  ['upsideByZone', 'downsideByZone'].forEach((key) => {
    const side = key === 'upsideByZone' ? 'upside' : 'downside';
    (cts?.[key]?.zones || []).forEach((z) => {
      if (z.total === 0) return;
      const where = `CTS ${side} reversals in ${z.zone.replace('_', ' ')}`;
      if (z.verdict === 'above') {
        confirmed.push({ key: `${key}-${z.zone}`, text:
          `${where}: ${pct(z.rate)} of ${plural(z.total, 'signal')}, `
          + `${fixed((z.edge || 0) * 100, 1)} points over the baseline. `
          + `The 95% interval (${pct(z.ci.low)}–${pct(z.ci.high)}) sits entirely above it.` });
      } else if (n(z.edge) !== null && z.edge > 0) {
        watching.push({ key: `${key}-${z.zone}`, text:
          `${where} are ${fixed(z.edge * 100, 1)} points over the baseline on `
          + `${plural(z.total, 'signal')} — promising, but the interval still overlaps it.` });
      }
    });
  });

  // ── the two filters, each judged on its LIFT and on both sides having a sample ─────────
  const filter = (lift, withN, withoutN, label, advice) => {
    if (n(lift) === null) return;
    if (Math.abs(lift) < MATERIAL_LIFT) return;
    const small = withN < MIN_COMPARABLE || withoutN < MIN_COMPARABLE;
    const direction = lift > 0
      ? `worth ${fixed(lift * 100, 1)} points — ${advice.positive}`
      : `COSTS ${fixed(Math.abs(lift) * 100, 1)} points — ${advice.negative}`;
    const text = `${label} would have been ${direction} (${withN} with, ${withoutN} without).`;
    if (small) {
      watching.push({ key: label, text: `${text} One side has fewer than ${MIN_COMPARABLE} `
        + 'observations, so this is not yet a comparison.' });
    } else {
      confirmed.push({ key: label, text });
    }
  };

  ['up', 'down'].forEach((dir) => {
    const d = cts?.withDivergence?.[dir];
    // `with` and `without` are always built by `analyseCts`, so this guard is about a report
    // handed in partially populated rather than about a case the engine produces. It reads one
    // level deeper than feels necessary because the alternative is a summary that throws on a
    // report the tables render perfectly well.
    if (!d || !d.with || !d.without) return;
    filter(d.lift, d.with.total, d.without.total,
      `Requiring an agreeing RSI divergence on ${dir === 'up' ? 'upside' : 'downside'} signals`,
      { positive: 'the signals it keeps resolved better than the ones it discards.',
        negative: 'the signals it would have discarded resolved better.' });
  });

  const v = report.volume?.cts;
  if (v && v.expanded && v.quiet) {
    filter(v.lift, v.expanded.n, v.quiet.n,
      'Requiring expanded volume at the signal bar',
      { positive: 'participation confirmed the move on this range.',
        negative: 'the quiet signals were the better ones here.' });
  }

  // ── a boundary layer worth fading ──────────────────────────────────────────────────────
  const best = report.band ? strongestLayer(report.band) : null;
  if (best && n(best.hold.rate) !== null && best.hold.rate > 0.5) {
    const text = `${best.layer} on the ${best.side} side turned price back `
      + `${pct(best.hold.rate)} of the ${plural(best.tests, 'time')} it was tested — `
      + 'the one level here that rejected more often than it gave way.';
    (best.tests >= MIN_COMPARABLE ? confirmed : watching).push({ key: 'layer', text });
  }

  return { confirmed, watching };
}

/** The layer with the highest hold rate, preferring one that was actually tested. */
function strongestLayer(band) {
  const all = [
    ...(band.boundaries?.up || []).map(l => ({ ...l, side: 'upper' })),
    ...(band.boundaries?.down || []).map(l => ({ ...l, side: 'lower' })),
  ].filter(l => l.tests > 0 && n(l.hold.rate) !== null);
  if (all.length === 0) return null;
  // Ties on the rate go to the layer with more tests, which is the better-measured one.
  return all.sort((a, b) => (b.hold.rate - a.hold.rate) || (b.tests - a.tests))[0];
}

export default narrate;
